import React, { useState, useEffect } from 'react'
import {
  Instagram,
  Music,
  Facebook,
  Twitter,
  Youtube,
  Linkedin,
  Plus,
  CheckCircle2
} from 'lucide-react'
import Modal from '../ui/Modal'
import { Button, Badge } from '../ui'
import { Organization, organizationService } from '../../services/organizationService'
import { customerService } from '../../services/customerService'
import { profileService, Profile } from '../../services/profileService'
import { Customer } from '../../types/customer'
import OrganizationPlatformAccountDrawer from '../support/OrganizationPlatformAccountDrawer'
import { useToast } from '../../contexts/ToastContext'
import usePermissionsStore from '../../stores/permissionsStore'

interface FirstTimeSetupModalProps {
  isOpen: boolean
  onAccountAdded: () => void
  organizationId: string
}

const supportedPlatforms = [
  { name: 'Instagram', icon: Instagram, color: 'text-pink-500' },
  { name: 'TikTok', icon: Music, color: 'text-black' },
  { name: 'Facebook', icon: Facebook, color: 'text-blue-600' },
  { name: 'Twitter', icon: Twitter, color: 'text-blue-400' },
  { name: 'YouTube', icon: Youtube, color: 'text-red-600' },
  { name: 'LinkedIn', icon: Linkedin, color: 'text-blue-700' }
]

const FirstTimeSetupModal: React.FC<FirstTimeSetupModalProps> = ({
  isOpen,
  onAccountAdded,
  organizationId
}) => {
  const { showError, showSuccess } = useToast()
  const { loadPermissions, permissions } = usePermissionsStore()

  const [organization, setOrganization] = useState<Organization | null>(null)
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [profiles, setProfiles] = useState<Profile[]>([])
  const [loading, setLoading] = useState(true)

  // Drawer state for adding accounts
  const [showDrawer, setShowDrawer] = useState(false)

  // Load organization and customer data
  useEffect(() => {
    if (!isOpen || !organizationId) return

    const loadData = async () => {
      setLoading(true)
      try {
        // Load organization
        const orgResponse = await organizationService.getOrganization(organizationId)
        if (orgResponse.success) {
          setOrganization(orgResponse.data)

          // Load permissions for this organization
          await loadPermissions(organizationId)

          // Load customer
          const customerResponse = await customerService.getCustomer(orgResponse.data.customerId)
          if (customerResponse.success) {
            setCustomer(customerResponse.data.customer)

            // Load existing profiles
            const profilesResponse = await profileService.getProfiles({
              customerId: customerResponse.data.customer.id,
              includeInactive: false
            })
            setProfiles(profilesResponse.data.profiles || [])
          }
        }
      } catch (error) {
        console.error('Error loading data:', error)
        showError('Failed to load data', 'Please try again')
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [isOpen, organizationId, loadPermissions, showError])

  const handleAddAccount = () => {
    setShowDrawer(true)
  }

  const handleDrawerClose = () => {
    setShowDrawer(false)
  }

  const handleAccountSaved = async () => {
    // Close the drawer
    setShowDrawer(false)

    // Account was successfully saved, complete the first-time setup
    showSuccess('Account added successfully!', 'You can now start tracking your social media analytics.')
    onAccountAdded()
  }

  const getPlatformIcon = (platformName: string) => {
    const platform = supportedPlatforms.find(
      p => p.name.toLowerCase() === platformName.toLowerCase()
    )
    return platform?.icon || Instagram
  }

  const getPlatformColor = (platformName: string) => {
    const platform = supportedPlatforms.find(
      p => p.name.toLowerCase() === platformName.toLowerCase()
    )
    return platform?.color || 'text-gray-500'
  }

  const maxAccounts = permissions?.limits.max_social_accounts || 0

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={() => {}} // No-op - modal cannot be closed without adding an account
        title="Welcome! Let's Set Up Your Account"
        size="lg"
        showCloseButton={false}
        closeOnOverlayClick={false}
        closeOnEscape={false}
      >
        <div className="space-y-6">
          {/* Welcome Message */}
          <div className="text-center">
            <p className="text-gray-600">
              To get started with AVI, connect at least one social media account.
              This allows us to track your analytics and provide valuable insights.
            </p>
          </div>

          {/* Supported Platforms */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              Supported Platforms
            </h3>
            <div className="flex flex-wrap gap-4 justify-center">
              {supportedPlatforms.map((platform) => {
                const Icon = platform.icon
                return (
                  <div
                    key={platform.name}
                    className="flex flex-col items-center gap-1"
                  >
                    {platform.name === 'TikTok' ? (
                      <img
                        src="/tiktok-logo.png"
                        alt="TikTok"
                        className="h-6 w-6"
                      />
                    ) : (
                      <Icon className={`h-6 w-6 ${platform.color}`} />
                    )}
                    <span className="text-xs text-gray-600">{platform.name}</span>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Added Accounts */}
          {profiles.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Connected Accounts ({profiles.length})
              </h3>
              <div className="space-y-2">
                {profiles.map((profile) => {
                  const Icon = getPlatformIcon(profile.platform.name)
                  const color = getPlatformColor(profile.platform.name)
                  return (
                    <div
                      key={profile.id}
                      className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg"
                    >
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      {profile.platform.name.toLowerCase() === 'tiktok' ? (
                        <img
                          src="/tiktok-logo.png"
                          alt="TikTok"
                          className="h-5 w-5"
                        />
                      ) : (
                        <Icon className={`h-5 w-5 ${color}`} />
                      )}
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">@{profile.username}</p>
                        <p className="text-xs text-gray-500">{profile.platform.name}</p>
                      </div>
                      <Badge variant="success">Connected</Badge>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Add Account Button */}
          <div className="flex flex-col items-center gap-4">
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-avi-primary"></div>
              </div>
            ) : (
              <>
                <Button
                  onClick={handleAddAccount}
                  variant="primary"
                  className="w-full max-w-sm"
                  disabled={profiles.length >= maxAccounts}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {profiles.length === 0 ? 'Add Your First Account' : 'Add Another Account'}
                </Button>

                {profiles.length > 0 && (
                  <Button
                    onClick={onAccountAdded}
                    variant="outline"
                    className="w-full max-w-sm"
                  >
                    Continue to Dashboard
                  </Button>
                )}

                {maxAccounts > 0 && (
                  <p className="text-xs text-gray-500 text-center">
                    {profiles.length} / {maxAccounts} accounts used in your plan
                  </p>
                )}
              </>
            )}
          </div>

          {/* Help Text */}
          <p className="text-xs text-gray-500 text-center">
            You can add more accounts later from the Settings page.
          </p>
        </div>
      </Modal>

      {/* Account Drawer */}
      {organization && customer && (
        <OrganizationPlatformAccountDrawer
          isOpen={showDrawer}
          onClose={handleDrawerClose}
          platformAccount={null}
          isCreating={true}
          customers={[customer]}
          organization={organization}
          onSaved={handleAccountSaved}
        />
      )}
    </>
  )
}

export default FirstTimeSetupModal
